/**
 * Appwrite endpoint / project configuration.
 *
 * These are the exact identifiers the `elalab` Flutter app uses (see
 * `lib/core/constants.dart`). The web app talks to the same project, the same
 * database and the same storage bucket, so a user sees identical data in both.
 *
 * NOTE: only client-side identifiers live here. Never put an Appwrite API
 * (server) key in the web app — it authenticates as the end user through the
 * normal client session mechanism.
 */

function required(value: string | undefined, name: string): string {
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `Missing environment variable ${name}. Copy .env.example to .env and fill it in.`,
    );
  }
  return value.trim();
}

export const appwriteConfig = {
  endpoint: required(import.meta.env.VITE_APPWRITE_ENDPOINT, 'VITE_APPWRITE_ENDPOINT'),
  projectId: required(import.meta.env.VITE_APPWRITE_PROJECT_ID, 'VITE_APPWRITE_PROJECT_ID'),

  /** Database that holds the meditation data. */
  databaseId: required(import.meta.env.VITE_APPWRITE_DATABASE_ID, 'VITE_APPWRITE_DATABASE_ID'),

  /** Collection holding one row per finished meditation session. */
  sessionSummariesCollectionId: required(
    import.meta.env.VITE_APPWRITE_SESSION_SUMMARIES_COLLECTION_ID,
    'VITE_APPWRITE_SESSION_SUMMARIES_COLLECTION_ID',
  ),

  /** Storage bucket holding the per-session EEG timeline / raw files. */
  eegBucketId: required(import.meta.env.VITE_APPWRITE_EEG_BUCKET_ID, 'VITE_APPWRITE_EEG_BUCKET_ID'),

  /**
   * Collection holding one profile row per user. Written by the ela app; ELA
   * Lab only ever reads it.
   */
  profilesCollectionId: required(
    import.meta.env.VITE_APPWRITE_PROFILES_COLLECTION_ID,
    'VITE_APPWRITE_PROFILES_COLLECTION_ID',
  ),

  /** Storage bucket holding profile images. */
  profileImagesBucketId: required(
    import.meta.env.VITE_APPWRITE_PROFILE_IMAGES_BUCKET_ID,
    'VITE_APPWRITE_PROFILE_IMAGES_BUCKET_ID',
  ),
} as const;
